import api, { tokenStore, errorCode, errorMessage, unwrap } from "./platformClient";
import { clearCache } from "../utils/cache";
import { dataUrlToBlob } from "../utils/image";

/**
 * ANTREAN ABSEN (OUTBOX) — absen tak hilang walau sinyal/server bermasalah.
 * ============================================================================
 * Masalah: user check-out di jalan (sinyal lemah) → request gagal/timeout → selfie,
 * GPS, dan jam absen hilang, user harus mengulang dari awal (atau lupa).
 *
 * Solusi: SETIAP absen disimpan dulu di IndexedDB (HP), baru dikirim.
 *  - Berhasil langsung  → item dihapus, perilaku sama seperti sebelumnya.
 *  - Gagal karena jaringan/timeout/server sibuk → item TETAP di HP dan dikirim ulang
 *    otomatis (backoff + jitter) saat koneksi pulih / app dibuka lagi. User melihat
 *    "Absen tersimpan, menunggu dikirim" — bukan error.
 *  - Ditolak server karena aturan (mis. di luar radius) → item dibuang & error
 *    ditampilkan seperti biasa (mengulang tak akan membantu).
 *
 * KEAMANAN DATA (dijaga bersama backend):
 *  - Setiap item punya `id` = client_request_id (UUID) yang DIPAKAI ULANG di semua
 *    percobaan → server idempoten: retry tak membuat absen ganda / DOUBLE_CHECKIN palsu.
 *  - Kiriman ulang membawa `captured_at` + `offline_queued=true`. Server hanya
 *    memakai waktu tangkap itu untuk kiriman tertunda (maks 24 jam) dan mencatat
 *    selisihnya untuk audit HRD.
 *  - Item terikat ke `userId` pembuatnya; akun lain di HP yang sama tak akan
 *    mengirimnya.
 *
 * KETERBATASAN: tanpa Background Sync (mis. iOS) antrean terkirim saat app dibuka.
 * Bila IndexedDB tak tersedia (mode Private), isSupported()=false dan alur lama
 * (langsung kirim) dipakai.
 * ============================================================================
 */

const DB_NAME = "hris_outbox";
const STORE = "attendance";
const CHANGED_EVENT = "hris:outbox-changed";
const SENT_EVENT = "hris:outbox-sent";

const LIVE_ATTEMPT_LOCK_MS = 30000;       // > timeout axios (20 dtk): cegah flush latar menyerobot percobaan langsung
const BASE_BACKOFF_MS = 5000;
const MAX_BACKOFF_MS = 5 * 60 * 1000;
const MAX_AGE_MS = 24 * 3600 * 1000;      // sama dengan batas backend (offlineMaxAgeHours)
const PURGE_AGE_MS = 7 * 24 * 3600 * 1000; // sampah lama dibersihkan

/* ── util kecil ─────────────────────────────────────────── */

export function uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  // Fallback (konteks non-HTTPS / browser lama): UUID v4 dari getRandomValues.
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

const dateFmt = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Jakarta", year: "numeric", month: "2-digit", day: "2-digit",
});
/** ISO/Date → "YYYY-MM-DD" (WIB). */
export const jakartaDate = (v) => dateFmt.format(new Date(v));

/**
 * ID user dari access token (klaim `sub`). Tanpa token (login offline / server
 * belum siap) pakai user tersimpan — `id`-nya sama dengan `sub`. null bila belum login.
 */
function currentUserId() {
  try {
    const token = tokenStore.getAccess();
    if (!token) return tokenStore.getUser()?.id || null;
    const p = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = atob(p.padEnd(p.length + ((4 - (p.length % 4)) % 4), "="));
    return JSON.parse(json)?.sub || null;
  } catch {
    return null;
  }
}

/** Jeda sebelum percobaan ke-(attempts+1): eksponensial + jitter, maks 5 menit. */
export function backoffMs(attempts) {
  const base = Math.min(MAX_BACKOFF_MS, BASE_BACKOFF_MS * 2 ** Math.min(attempts, 10));
  return Math.round(base * (0.5 + Math.random()));
}

/**
 * Klasifikasi error kirim:
 *  - retry    : jaringan/timeout/server sibuk → simpan & coba lagi nanti.
 *  - auth     : sesi habis → simpan, tunggu login ulang.
 *  - reupload : selfie yang sudah di-upload hilang di server → upload ulang.
 *  - fatal    : ditolak aturan bisnis (4xx) → mengulang percuma.
 */
export function classify(err) {
  // Bukan error HTTP (mis. data selfie rusak) → mengulang percuma, jangan antre selamanya.
  if (!err?.isAxiosError) return "fatal";
  if (!err.response) return "retry"; // tak ada respons: offline, timeout, DNS, CORS
  const status = err.response.status;
  const code = errorCode(err);
  if (status === 401) return "auth";
  if ([408, 425, 429, 500, 502, 503, 504].includes(status)) return "retry";
  if (code === "UPLOAD_NOT_FOUND" || code === "UPLOAD_FORBIDDEN") return "reupload";
  return "fatal";
}

/* ── IndexedDB ──────────────────────────────────────────── */

let dbPromise = null;
function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    try {
      if (typeof indexedDB === "undefined") return reject(new Error("IndexedDB tidak tersedia"));
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: "id" });
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error || new Error("Gagal membuka IndexedDB"));
      req.onblocked = () => reject(new Error("IndexedDB terblokir"));
    } catch (e) {
      reject(e);
    }
  });
  dbPromise.catch(() => { dbPromise = null; });
  return dbPromise;
}

async function run(mode, fn) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    let result;
    try {
      const r = fn(t.objectStore(STORE));
      if (r) r.onsuccess = () => { result = r.result; };
    } catch (e) {
      reject(e);
      return;
    }
    t.oncomplete = () => resolve(result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error || new Error("Transaksi IndexedDB dibatalkan"));
  });
}

const notify = () => {
  try { window.dispatchEvent(new Event(CHANGED_EVENT)); } catch { /* bukan browser */ }
};
const getAll = async () => (await run("readonly", (s) => s.getAll())) || [];
const getOne = (id) => run("readonly", (s) => s.get(id));
const putItem = async (item) => { await run("readwrite", (s) => s.put(item)); notify(); };
const delItem = async (id) => { await run("readwrite", (s) => s.delete(id)); notify(); };

let supportedPromise = null;
/** IndexedDB benar-benar bisa dipakai (uji tulis)? False di sebagian mode Private. */
export function isSupported() {
  if (!supportedPromise) {
    supportedPromise = (async () => {
      try {
        await run("readwrite", (s) => s.put({ id: "__probe__" }));
        await run("readwrite", (s) => s.delete("__probe__"));
        return true;
      } catch {
        return false;
      }
    })();
  }
  return supportedPromise;
}

const byCapturedAt = (a, b) => (a.capturedAt < b.capturedAt ? -1 : a.capturedAt > b.capturedAt ? 1 : 0);

/* ── kirim satu item ────────────────────────────────────── */

/** Upload selfie (base64/dataURL) → storage key. Dipakai antrean & alur cadangan. */
export async function uploadSelfie(imageB64) {
  const form = new FormData();
  form.append("file", dataUrlToBlob(imageB64), "selfie.jpg");
  const data = await unwrap(
    api.post("/uploads/attendance", form, { headers: { "Content-Type": undefined }, timeout: 45000 })
  );
  return data?.key;
}

const inFlight = new Set(); // id item yang sedang dikirim di tab ini

async function sendItem(item, { live }) {
  let photoKey = item.photoKey;
  if (!photoKey && item.photoB64) {
    photoKey = await uploadSelfie(item.photoB64);
    // Simpan key SEGERA: kalau langkah absen gagal, retry tak perlu upload ulang.
    await putItem({ ...(await getOne(item.id) || item), photoKey });
  }
  const path = item.type === "in" ? "/attendance/checkin" : "/attendance/checkout";
  return unwrap(api.post(path, {
    ...item.payload,
    photo_key: photoKey,
    client_request_id: item.id,
    captured_at: item.capturedAt,
    offline_queued: !live, // percobaan pertama = langsung (jam server); ulang = tertunda
  }));
}

/** Catat kegagalan sementara pada item (baca versi terbaru dari DB agar photoKey tak hilang). */
async function markRetry(id, err, kind) {
  const cur = await getOne(id);
  if (!cur) return;
  const attempts = kind === "auth" ? cur.attempts : cur.attempts + 1;
  const wait = kind === "auth" ? 30000 : kind === "reupload" ? 1000 : backoffMs(attempts);
  await putItem({
    ...cur,
    photoKey: kind === "reupload" ? null : cur.photoKey,
    attempts,
    nextAttemptAt: Date.now() + wait,
    lastError: kind === "auth" ? "Sesi berakhir, login ulang untuk mengirim" : errorMessage(err, "Gagal terkirim"),
  });
}

async function markFailed(id, err) {
  const cur = await getOne(id);
  if (!cur) return;
  await putItem({ ...cur, status: "failed", lastError: errorMessage(err, "Ditolak server"), failedCode: errorCode(err) });
}

/* ── flush (kirim ulang antrean) ────────────────────────── */

let flushing = false;
let timer = null;

async function withLock(fn) {
  // Web Locks (bila ada): hanya SATU tab yang mengirim antrean pada satu waktu.
  if (typeof navigator !== "undefined" && navigator.locks?.request) {
    return navigator.locks.request("hris-outbox-flush", { ifAvailable: true }, (lock) => (lock ? fn() : undefined));
  }
  return fn();
}

async function pendingForCurrentUser() {
  const uid = currentUserId();
  if (!uid) return [];
  return (await getAll()).filter((i) => i.userId === uid && i.status === "pending").sort(byCapturedAt);
}

async function scheduleNext() {
  clearTimeout(timer);
  try {
    const items = await pendingForCurrentUser();
    if (!items.length) return;
    const next = Math.min(...items.map((i) => i.nextAttemptAt || 0));
    timer = setTimeout(() => flush(), Math.max(1000, next - Date.now()));
  } catch { /* IndexedDB bermasalah: pemicu online/focus tetap jalan */ }
}

/**
 * Kirim ulang item yang tertahan, BERURUTAN (jam tangkap) supaya check-in selalu
 * mendahului check-out. Berhenti di kegagalan sementara pertama (jangan melompati).
 * @param {{force?: boolean}} [opts] force = abaikan jeda backoff (mis. koneksi baru pulih).
 */
export async function flush({ force = false } = {}) {
  if (flushing) return;
  if (!force && typeof navigator !== "undefined" && navigator.onLine === false) return;
  flushing = true;
  let sent = 0;
  try {
    if (!(await isSupported())) return;
    await withLock(async () => {
      for (const item of await pendingForCurrentUser()) {
        if (inFlight.has(item.id)) continue;
        if (!force && (item.nextAttemptAt || 0) > Date.now()) break;

        if (Date.now() - new Date(item.capturedAt).getTime() > MAX_AGE_MS) {
          // Lewat batas 24 jam server → pasti ditolak; beri tahu user lebih awal.
          await putItem({ ...item, status: "failed", lastError: "Absen tertunda lebih dari 24 jam dan tidak bisa dikirim otomatis. Hubungi HRD untuk koreksi.", failedCode: "CAPTURED_TOO_OLD" });
          continue;
        }

        inFlight.add(item.id);
        try {
          await sendItem(item, { live: false });
          await delItem(item.id);
          sent += 1;
        } catch (err) {
          const kind = classify(err);
          if (kind === "fatal") { await markFailed(item.id, err); continue; }
          await markRetry(item.id, err, kind);
          break;
        } finally {
          inFlight.delete(item.id);
        }
      }
    });
  } finally {
    flushing = false;
    if (sent > 0) {
      clearCache("attendance_today");
      clearCache("attendance_history");
      clearCache("dashboard_summary");
      try {
        window.dispatchEvent(new CustomEvent(SENT_EVENT, { detail: { count: sent } }));
        window.dispatchEvent(new CustomEvent("hris:app-refresh", { detail: { reason: "outbox-flushed" } }));
      } catch { /* bukan browser */ }
    }
    scheduleNext();
  }
}

/* ── API untuk lapisan lain ─────────────────────────────── */

/**
 * Simpan lalu kirim satu absen.
 * @param {"in"|"out"} type
 * @param {{image?: string, fields: object}} input  image = base64/dataURL selfie
 * @returns {Promise<{status:"sent", record:object}|{status:"queued", item:object}>}
 * @throws error server (mis. di luar radius) — item dibuang, tampilkan ke user.
 */
export async function submit(type, { image, fields }) {
  const now = Date.now();
  const item = {
    id: uuid(),
    userId: currentUserId(),
    type,
    payload: fields,
    photoB64: image || null,
    photoKey: null,
    capturedAt: new Date(now).toISOString(),
    attempts: 0,
    nextAttemptAt: now + LIVE_ATTEMPT_LOCK_MS, // flush latar/tab lain tak menyerobot percobaan langsung
    status: "pending",
    lastError: null,
    createdAt: now,
  };

  await putItem(item); // simpan SEBELUM kirim: tab ditutup/HP mati di tengah jalan pun tak hilang
  inFlight.add(item.id);
  try {
    const record = await sendItem(item, { live: true });
    await delItem(item.id);
    return { status: "sent", record };
  } catch (err) {
    const kind = classify(err);
    if (kind === "fatal") {
      await delItem(item.id);
      throw err;
    }
    await markRetry(item.id, err, kind);
    scheduleNext();
    return { status: "queued", item: (await getOne(item.id)) || item };
  } finally {
    inFlight.delete(item.id);
  }
}

/** Bentuk record untuk UI dari item yang masih antre (selaras dengan bentuk record server). */
export function queuedView(item) {
  const p = item.payload || {};
  const base = { queued: true, pending_sync: true, date: jakartaDate(item.capturedAt) };
  return item.type === "in"
    ? { ...base, check_in: item.capturedAt, checkin_address: p.address || null, work_mode: p.work_mode, is_wfh: p.work_mode === "wfh" }
    : { ...base, check_out: item.capturedAt, checkout_address: p.address || null, worked_hours: null };
}

/**
 * Tempelkan absen yang masih antre (hari ini, WIB) ke record "hari ini" dari server,
 * supaya UI tak mengira "belum absen" dan user tak menekan tombol yang sama dua kali.
 * Mengembalikan `record` apa adanya bila tak ada antrean / IndexedDB bermasalah.
 */
export async function mergePendingIntoToday(record) {
  try {
    if (!(await isSupported())) return record;
    const today = jakartaDate(new Date());
    const items = (await pendingForCurrentUser()).filter((i) => jakartaDate(i.capturedAt) === today);
    if (!items.length) return record;

    const merged = { date: today, ...(record || {}) };
    for (const i of items) {
      const v = queuedView(i);
      if (i.type === "in" && !merged.check_in) Object.assign(merged, v);
      if (i.type === "out" && !merged.check_out) Object.assign(merged, v);
    }
    merged.pending_sync = true;
    if (merged.check_in && merged.check_out && merged.worked_hours == null) {
      merged.worked_hours = (new Date(merged.check_out) - new Date(merged.check_in)) / 3600000;
    }
    return merged;
  } catch {
    return record;
  }
}

/** Item milik user saat ini (pending + failed) — untuk banner. */
export async function listForCurrentUser() {
  try {
    const uid = currentUserId();
    if (!uid || !(await isSupported())) return [];
    return (await getAll()).filter((i) => i.userId === uid).sort(byCapturedAt);
  } catch {
    return [];
  }
}

/** Buang item (mis. user menutup pemberitahuan absen yang gagal). */
export async function dismiss(id) {
  try { await delItem(id); } catch { /* abaikan */ }
}

let started = false;
/** Pasang pemicu kirim ulang (idempoten): online, fokus, app kembali terlihat. */
export function start() {
  if (started || typeof window === "undefined") return;
  started = true;
  const kick = () => flush({ force: true });
  window.addEventListener("online", kick);
  window.addEventListener("focus", kick);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") kick();
  });
  // Bersihkan sampah lama (mis. item user lain yang tak pernah login lagi), lalu coba kirim.
  (async () => {
    try {
      if (!(await isSupported())) return;
      for (const i of await getAll()) {
        if (Date.now() - (i.createdAt || 0) > PURGE_AGE_MS) await delItem(i.id);
      }
    } catch { /* abaikan */ }
    setTimeout(kick, 1500);
  })();
}

export const outbox = {
  isSupported, submit, queuedView, mergePendingIntoToday, listForCurrentUser,
  flush, dismiss, start, uploadSelfie, uuid,
};
export default outbox;
